import { useTranslation } from 'react-i18next'
import styled, { ThemeProvider } from 'styled-components'
import { theme } from 'styles/theme'
import { SUPPORT_EMAIL } from 'Lib/Constants'

// Fallback for the top-level Sentry.ErrorBoundary. It renders OUTSIDE the app's
// ThemeProvider / GlobalStyle / Redux Provider, so it brings its own ThemeProvider
// and sets font-family explicitly. useSuspense must stay false: the boundary can
// catch before translations load, and a suspending fallback with no Suspense
// ancestor would blank the page — the English defaults render instead.

const Wrapper = styled.div`
  font-family: 'Fira Sans', sans-serif;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${(props) => props.theme.spacing.large};
  color: ${(props) => props.theme.colors.text};
  background: ${(props) => props.theme.colors.white};
`

const Content = styled.div`
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.theme.spacing.medium};
`

const Emoji = styled.div`
  font-size: 48px;
  line-height: 1;
`

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.grey900};
`

const Description = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
`

const ReloadButton = styled.button`
  margin-top: ${(props) => props.theme.spacing.small};
  padding: 14px 32px;
  border: none;
  border-radius: 30px;
  background: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.basicWhite};
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.colors.primaryDark};
  }
`

const SupportLink = styled.a`
  color: ${(props) => props.theme.colors.primary};
  text-decoration: none;
`

const WARNING_EMOJI = '⚠️'

const ErrorScreen = () => {
  const { t } = useTranslation(undefined, { useSuspense: false })

  return (
    <ThemeProvider theme={theme}>
      <Wrapper>
        <Content>
          <Emoji role="img" aria-label="warning">{WARNING_EMOJI}</Emoji>
          <Title>{t('errorScreen.title', 'Something went wrong')}</Title>
          <Description>
            {t(
              'errorScreen.description',
              'An unexpected error occurred. Please reload the page to continue.'
            )}
          </Description>
          <ReloadButton onClick={() => window.location.reload()}>
            {t('errorScreen.reloadButton', 'Reload page')}
          </ReloadButton>
          <Description>
            {t('errorScreen.supportPrefix', 'Still stuck? Contact us at')}{' '}
            <SupportLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</SupportLink>
          </Description>
        </Content>
      </Wrapper>
    </ThemeProvider>
  )
}

export default ErrorScreen
